// ============================================
// Web Push (issue #61 fase 2) — glue browser + API
// ============================================
// O opt-in é por DISPOSITIVO (PushSubscription do browser) e vale para a conta
// inteira — o servidor resolve o alcance por empresa via memberships na hora do
// disparo. O SW registrado aqui é PUSH-ONLY (sem cache offline — decisão do
// revamp mobile de 28/08: dado stale numa plataforma de monitoramento é risco).

import { apiFetch } from './api'

export type PushUiState =
  | 'unsupported' // browser sem service worker / Push API
  | 'ios-needs-install' // iOS fora de PWA instalada (Web Push só na instalada)
  | 'server-off' // servidor sem chaves VAPID
  | 'denied' // permissão de notificação negada no browser
  | 'off' // suportado e liberado, sem inscrição neste dispositivo
  | 'on' // inscrito neste dispositivo

export interface PushServerConfig {
  enabled: boolean
  publicKey: string
}

export interface PushTestResult {
  ok: boolean
  sent: number
  failed: number
  reason?: string
}

/** Chave VAPID (base64url) → bytes para o pushManager.subscribe. */
export function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4)
  const b64 = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/')
  const raw = atob(b64)
  const arr = new Uint8Array(raw.length)
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i)
  return arr
}

const isSupported = (): boolean =>
  'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window

// iPadOS moderno se apresenta como Mac — o toque multiponto entrega.
const isIos = (): boolean =>
  /iPad|iPhone|iPod/.test(navigator.userAgent) ||
  (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)

const isStandalone = (): boolean =>
  window.matchMedia?.('(display-mode: standalone)').matches === true ||
  (navigator as unknown as { standalone?: boolean }).standalone === true

const getLocalSubscription = async (): Promise<PushSubscription | null> => {
  const reg = await navigator.serviceWorker.getRegistration()
  return (await reg?.pushManager.getSubscription()) ?? null
}

export const pushService = {
  /**
   * Chamado no boot do app: mantém o SW do dispositivo JÁ inscrito atualizado.
   * NÃO inscreve ninguém — a inscrição é só por opt-in no diálogo.
   */
  registerServiceWorker(): void {
    if (!('serviceWorker' in navigator)) return
    // http fora de localhost não tem secure context — sem push mesmo; silencioso.
    navigator.serviceWorker.register('/sw.js').catch(() => {})
  },

  async getServerConfig(): Promise<PushServerConfig> {
    return apiFetch<PushServerConfig>('/api/raiz/push/config')
  },

  /** Estado para a UI, do impedimento mais duro para o mais brando. */
  async getState(): Promise<PushUiState> {
    if (isIos() && !isStandalone() && !isSupported()) return 'ios-needs-install'
    if (!isSupported()) return 'unsupported'
    const cfg = await this.getServerConfig()
    if (!cfg.enabled) return 'server-off'
    if (Notification.permission === 'denied') return 'denied'
    return (await getLocalSubscription()) != null ? 'on' : 'off'
  },

  /** Opt-in: registra SW, pede permissão, inscreve e grava no servidor. */
  async enable(): Promise<PushUiState> {
    const cfg = await this.getServerConfig()
    if (!cfg.enabled) return 'server-off'
    const reg = await navigator.serviceWorker.register('/sw.js')
    const permission = await Notification.requestPermission()
    if (permission !== 'granted') return 'denied'
    await navigator.serviceWorker.ready
    const sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(cfg.publicKey).buffer as ArrayBuffer,
    })
    try {
      await apiFetch('/api/raiz/push/subscriptions', {
        method: 'POST',
        body: JSON.stringify(sub.toJSON()),
      })
    } catch (err) {
      // Servidor não gravou → desfaz o local, senão a UI mostraria "ativo"
      // num dispositivo que nunca receberá nada.
      await sub.unsubscribe().catch(() => {})
      throw err
    }
    return 'on'
  },

  /** Opt-out: remove no servidor e desinscreve o browser. */
  async disable(): Promise<void> {
    const sub = await getLocalSubscription()
    if (sub == null) return
    await apiFetch('/api/raiz/push/subscriptions', {
      method: 'DELETE',
      body: JSON.stringify({ endpoint: sub.endpoint }),
    })
    await sub.unsubscribe().catch(() => {})
  },

  /** Dispara uma notificação de teste nos dispositivos do próprio usuário. */
  async sendTest(): Promise<PushTestResult> {
    return apiFetch<PushTestResult>('/api/raiz/push/test', { method: 'POST', body: '{}' })
  },
}
